import {publishOptimisticFields} from '@github-ui/optimistic-connection/connectionMutationBridge'
import {commitMutation, graphql} from 'react-relay'
import type {Environment, RecordSourceSelectorProxy} from 'relay-runtime'

import type {deleteIssueCommentMutation} from './__generated__/deleteIssueCommentMutation.graphql'
import {beginDeleteCommentOverlay, type OptimisticConnection} from './comment-connection-overlay'

function updateTotalCount(store: RecordSourceSelectorProxy, connectionIds: string[]) {
  for (const connectionId of connectionIds) {
    const connection = store.get(connectionId)
    if (connection) {
      const previousCount = Number(connection.getValue('totalCount'))
      if (!isNaN(previousCount) && previousCount > 0) {
        connection.setValue(previousCount - 1, 'totalCount')
      }
    }
  }
}

export function commitDeleteIssueCommentMutation({
  environment,
  connectionIds,
  issueId,
  input: {id},
  onCompleted,
  onError,
  optimisticConnection,
}: {
  environment: Environment
  connectionIds: string[]
  issueId?: string
  input: {id: string}
  onCompleted?: () => void
  onError?: (error?: Error) => void
  optimisticConnection?: OptimisticConnection
}) {
  const overlay = optimisticConnection
    ? beginDeleteCommentOverlay({connection: optimisticConnection, nodeId: id})
    : undefined
  const commentCountOperation = issueId
    ? ({
        type: 'deltaEntityField',
        id: issueId,
        field: 'comments',
        delta: -1,
        clamp: {min: 0},
      } as const)
    : undefined
  const publication = publishOptimisticFields(commentCountOperation)

  try {
    return commitMutation<deleteIssueCommentMutation>(environment, {
      // eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
      mutation: graphql`
        mutation deleteIssueCommentMutation($connections: [ID!]!, $input: DeleteIssueCommentInput!) @raw_response_type {
          deleteIssueComment(input: $input) {
            clientMutationId
            issueComment {
              id @deleteEdge(connections: $connections)
              issue {
                id
                totalCommentsCount
              }
            }
          }
        }
      `,
      variables: {
        input: {id},
        connections: connectionIds,
      },
      optimisticResponse: {
        deleteIssueComment: {
          clientMutationId: '',
          issueComment: {
            id,
            issue: {
              id: issueId ?? '',
              totalCommentsCount: null,
            },
          },
        },
      },
      optimisticUpdater: store => updateTotalCount(store, connectionIds),
      updater: store => updateTotalCount(store, connectionIds),
      onCompleted: response => {
        if (response.deleteIssueComment == null) {
          overlay?.fail()
          publication.failed(new Error('Failed to delete comment'))
          onError?.()
        } else {
          overlay?.settle()
          const issue = response.deleteIssueComment.issueComment?.issue
          if (commentCountOperation && issue?.totalCommentsCount != null) {
            // Replace the relative optimistic delta with the server's absolute count. The shared
            // operation id keeps the pending delta from applying again before settlement finishes.
            publication.completed({
              type: 'setFields',
              id: issue.id,
              fields: {comments: issue.totalCommentsCount},
            })
          } else if (commentCountOperation) {
            publication.failed(new Error('Comment count was missing from the response'))
          }
          onCompleted?.()
        }
      },
      onError: error => {
        overlay?.fail(error)
        publication.failed(error)
        onError?.(error)
      },
      onUnsubscribe: () => {
        overlay?.fail(new Error('Deleting the comment was cancelled'))
        publication.failed(new Error('Deleting comment was cancelled'))
      },
    })
  } catch (error) {
    // A throw out of the commit reaches no callback, so the row would stay hidden for good.
    overlay?.fail(error instanceof Error ? error : new Error('Deleting the comment failed'))
    publication.failed(error instanceof Error ? error : new Error('Failed to delete comment'))
    throw error
  }
}
